import React from 'react';
import { motion } from 'framer-motion';

function EventRules() {
  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 sm:py-16 px-4">
      <motion.div 
        className="max-w-3xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header Section */}
        <motion.div variants={itemVariants} className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Event Rules & Regulations
          </h1>
          <p className="text-lg text-gray-600">
            Please read these rules carefully before participating in any event
          </p>
        </motion.div>

        {/* Main Content */}
        <motion.div 
          className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
          variants={itemVariants}
        >
          {/* General Rules Section */}
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              1. General Rules
            </h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Participants must arrive at least 30 minutes before the event start time.</span>
              </li>
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Valid ID proof is mandatory for all participants.</span>
              </li>
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>The decision of event organizers will be final and binding.</span>
              </li>
            </ul>
          </div>

          {/* Registration & Participation */}
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              2. Registration & Participation
            </h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Registration is non-transferable and must be completed before the deadline.</span>
              </li>
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Participants must follow all safety guidelines provided by the organizers.</span>
              </li>
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Age restrictions and eligibility criteria may apply for specific events.</span>
              </li>
            </ul>
          </div>

          {/* Code of Conduct */}
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              3. Code of Conduct
            </h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Maintain professional and respectful behavior throughout the event.</span>
              </li>
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Any form of discrimination or harassment will not be tolerated.</span>
              </li>
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Follow all venue rules and regulations.</span>
              </li>
            </ul>
          </div>

          {/* Prohibited Items */}
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              4. Prohibited Items & Behavior
            </h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>No weapons or dangerous items of any kind.</span>
              </li>
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Photography or recording without prior permission is prohibited.</span>
              </li>
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Disruptive behavior will result in immediate removal from the event.</span>
              </li>
            </ul>
          </div>

          {/* Cancellation & Refunds */}
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              5. Cancellation & Modifications
            </h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Organizers reserve the right to modify or cancel the event due to unforeseen circumstances.</span>
              </li>
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Refunds will be processed according to the refund policy.</span>
              </li>
              <li className="flex items-start">
                <span className="h-6 flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                </span>
                <span>Changes to event schedule or venue will be communicated via registered email.</span>
              </li>
            </ul>
          </div>
        </motion.div>

        {/* Footer Note */}
        <motion.p 
          variants={itemVariants}
          className="mt-8 text-center text-gray-500 text-sm"
        >
          These rules are subject to change. Please check event details for specific rules and regulations for each event.
        </motion.p>
      </motion.div>
    </div>
  );
}

export default EventRules; 